import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { ChevronDown, ChevronRight, ShieldAlert } from "lucide-react";

import { EmptyState } from "@/components/shared/EmptyState";
import { LoadingLogo } from "@/components/shared/LoadingLogo";
import { PageHeader } from "@/components/shared/PageHeader";
import { TableCard } from "@/components/shared/TableCard";
import { TablePagination } from "@/components/shared/TablePagination";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { usePaginationFromUrl } from "@/hooks/usePaginationFromUrl";
import { formatDisplayDate } from "@/lib/dateFormat";
import {
  getModerationFlagsForUser,
  listModerationFlags,
  type ModerationFlagSummary,
} from "@/services/platformModerationService";

/**
 * Who has tried to move a deal off the platform, and what they wrote
 * (spec ce646966 §6 M4).
 *
 * READ-ONLY by design. There is no suspend or ban here, and no way to dismiss
 * a flag: spec §3d and acceptance #8 say a flag never changes a
 * professional's ability to bid without an admin acting, and no suspended
 * state exists to move anyone into. The job of this page is to let a human
 * see — the action that follows is theirs, taken elsewhere.
 */
export function PlatformModerationFlagsPage(): JSX.Element {
  const { t } = useTranslation(undefined, { keyPrefix: "platformModerationFlags" });
  const pagination = usePaginationFromUrl();
  const [expandedKey, setExpandedKey] = useState<string | null>(null);

  const listQuery = useQuery({
    queryKey: ["platform", "moderation-flags", pagination.page, pagination.limit],
    queryFn: () =>
      listModerationFlags({
        limit: pagination.limit,
        offset: (pagination.page - 1) * pagination.limit,
      }),
  });

  useEffect(() => {
    if (listQuery.data != null) {
      pagination.setTotal(listQuery.data.total);
    } else if (listQuery.isError) {
      pagination.setTotal(0);
    }
  }, [listQuery.data, listQuery.isError, pagination]);

  useEffect(() => {
    if (listQuery.isError) {
      toast.error(t("loadError"));
    }
  }, [listQuery.isError, t]);

  const items = listQuery.data?.items ?? [];

  return (
    <section className="space-y-6">
      <PageHeader title={t("title")} description={t("description")} />

      {listQuery.isLoading ? (
        <LoadingLogo />
      ) : items.length === 0 ? (
        <EmptyState title={t("emptyTitle")} message={t("emptyMessage")} />
      ) : (
        <>
          <TableCard>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8" />
                  <TableHead>{t("columnPerson")}</TableHead>
                  <TableHead>{t("columnRole")}</TableHead>
                  <TableHead className="text-right">{t("columnFlags")}</TableHead>
                  <TableHead>{t("columnChannels")}</TableHead>
                  <TableHead>{t("columnLastAttempt")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((row) => (
                  <FlagRow
                    key={`${row.userId}:${row.role}`}
                    row={row}
                    isExpanded={expandedKey === `${row.userId}:${row.role}`}
                    onToggle={() =>
                      setExpandedKey((current) =>
                        current === `${row.userId}:${row.role}`
                          ? null
                          : `${row.userId}:${row.role}`,
                      )
                    }
                  />
                ))}
              </TableBody>
            </Table>
          </TableCard>

          <TablePagination
            pagination={pagination}
            rowsPerPageLabel={t("rowsPerPage")}
            pageOfLabel={t("pageOf")}
            onRefresh={() => void listQuery.refetch()}
            isRefreshing={listQuery.isFetching}
          />
        </>
      )}
    </section>
  );
}

/**
 * One person, and their messages underneath when opened.
 *
 * Expanding in place rather than routing to a detail page: the evidence is a
 * handful of short messages, and a second screen would put a navigation step
 * between the count and the thing that justifies it. Keeping both on screen
 * together is the point.
 */
function FlagRow({
  row,
  isExpanded,
  onToggle,
}: {
  row: ModerationFlagSummary;
  isExpanded: boolean;
  onToggle: () => void;
}): JSX.Element {
  const { t } = useTranslation(undefined, { keyPrefix: "platformModerationFlags" });

  // Fetched only once opened: most rows are never expanded, and the message
  // bodies are the largest thing this screen can load.
  const detailQuery = useQuery({
    queryKey: ["platform", "moderation-flags", row.userId],
    queryFn: () => getModerationFlagsForUser(row.userId),
    enabled: isExpanded,
  });

  // The list groups by (person, role) but the detail endpoint is per person,
  // so this filters to the role of the row that was opened — otherwise an
  // expanded professional row would show that person's consumer attempts as
  // though they were the same conduct.
  const detailItems = (detailQuery.data?.items ?? []).filter((d) => d.role === row.role);

  return (
    <>
      <TableRow
        className="cursor-pointer"
        onClick={onToggle}
        aria-expanded={isExpanded}
      >
        <TableCell>
          {isExpanded ? (
            <ChevronDown className="h-4 w-4 text-muted-foreground" aria-hidden />
          ) : (
            <ChevronRight className="h-4 w-4 text-muted-foreground" aria-hidden />
          )}
        </TableCell>
        <TableCell>
          <span className="font-medium">{row.displayName ?? t("unnamedPerson")}</span>
          {row.email ? (
            <span className="block text-xs text-muted-foreground">{row.email}</span>
          ) : null}
        </TableCell>
        <TableCell>{t(`role_${row.role}`)}</TableCell>
        <TableCell className="text-right">
          <span className="font-medium">{row.flagCount}</span>
          {row.flagCount !== row.blockedCount ? (
            // Blocked and flagged are different conduct: one never reached the
            // other party, the other did. A single total would hide that.
            <span className="block text-xs text-muted-foreground">
              {t("blockedOfTotal", { blocked: row.blockedCount })}
            </span>
          ) : null}
        </TableCell>
        <TableCell className="text-xs text-muted-foreground">
          {row.channels.map((c) => t(`channel_${c}`)).join(", ")}
        </TableCell>
        <TableCell className="text-xs text-muted-foreground">
          {formatDisplayDate(row.lastBlockedAt)}
        </TableCell>
      </TableRow>

      {isExpanded ? (
        <TableRow>
          <TableCell colSpan={6} className="bg-muted/30">
            {detailQuery.isLoading ? (
              <p className="py-3 text-sm text-muted-foreground">{t("detailLoading")}</p>
            ) : detailItems.length === 0 ? (
              <EmptyState title={t("detailEmpty")} className="py-6" />
            ) : (
              <ul className="space-y-3 py-2">
                {detailItems.map((detail) => (
                  <li
                    key={detail.id}
                    className="rounded-md border border-border bg-background p-3"
                  >
                    <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                      <ShieldAlert className="h-3.5 w-3.5 text-destructive" aria-hidden />
                      <span
                        className={
                          detail.action === "blocked"
                            ? "font-medium text-destructive"
                            : "font-medium text-foreground"
                        }
                      >
                        {t(`action_${detail.action}`)}
                      </span>
                      <span aria-hidden>·</span>
                      <span>{t(`channel_${detail.channel}`)}</span>
                      <span aria-hidden>·</span>
                      <span>{formatDisplayDate(detail.createdAt)}</span>
                      {detail.aiScore != null ? (
                        <>
                          <span aria-hidden>·</span>
                          <span>{t("aiScore", { score: detail.aiScore.toFixed(2) })}</span>
                        </>
                      ) : null}
                      {detail.details.length > 0 ? (
                        <>
                          <span aria-hidden>·</span>
                          <span>
                            {detail.details.map((d) => t(`kind_${d.kind}`)).join(", ")}
                          </span>
                        </>
                      ) : null}
                    </div>
                    {/* Verbatim, and wrapped rather than truncated: judging
                        whether the scan was right means reading the whole
                        message, not a preview of it. */}
                    <p className="mt-2 whitespace-pre-wrap break-words text-sm">
                      {detail.content}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </TableCell>
        </TableRow>
      ) : null}
    </>
  );
}
